import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function getErrorMessage(err: unknown): string {
  if (!err) return "Произошла ошибка, попробуй ещё раз";
  if (typeof err === "string") return err || "Произошла ошибка, попробуй ещё раз";
  if (typeof err === "object") {
    const anyErr = err as { message?: unknown; error_description?: unknown };
    const msg =
      (typeof anyErr.message === "string" && anyErr.message) ||
      (typeof anyErr.error_description === "string" && anyErr.error_description) ||
      "";
    if (msg) return msg;
  }
  return "Произошла ошибка, попробуй ещё раз";
}

const schema = z
  .object({
    password: z.string().min(8, "Минимум 8 символов"),
    confirm: z.string().min(8, "Минимум 8 символов"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Пароли не совпадают",
    path: ["confirm"],
  });

type Values = z.infer<typeof schema>;

function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={show ? "text" : "password"} className="pr-10" />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Скрыть пароль" : "Показать пароль"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  useEffect(() => {
    // Supabase auto-parses the recovery hash and fires PASSWORD_RECOVERY.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        setHasRecoverySession(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setHasRecoverySession((prev) => prev ?? !!data.session);
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(values: Values) {
    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password: values.password });
    setIsLoading(false);
    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }
    toast.success("Пароль обновлён");
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      navigate({ to: "/home", replace: true });
    } else {
      navigate({ to: "/auth", replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-white px-4 py-12">
      <div className="mx-auto max-w-[420px]">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block">
            <span className="font-brand text-2xl font-bold text-td-dark">TurkeyDrive</span>
          </Link>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Новый пароль</CardTitle>
            <CardDescription>
              Придумай новый пароль для входа в аккаунт
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasRecoverySession === false ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Ссылка для сброса недействительна или устарела. Запроси новую
                  ссылку на странице входа.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth">Вернуться ко входу</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Новый пароль</Label>
                  <PasswordInput id="new-password" {...form.register("password")} />
                  {form.formState.errors.password && (
                    <p className="text-sm text-red-500">
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Повтори пароль</Label>
                  <PasswordInput id="confirm-password" {...form.register("confirm")} />
                  {form.formState.errors.confirm && (
                    <p className="text-sm text-red-500">
                      {form.formState.errors.confirm.message}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Сохраняем..." : "Сохранить пароль"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
